import { call, put, takeEvery, select } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";
import Debug from "debug";
import axios from "axios";

import { ACTIONS } from "../actions/constants";
import { extractResponse } from "./helpers";
import {
  ErrorState,
  EditorAuthResponse,
  ActionEditorAuthGet,
  User,
  ActionEditorAuthRemove,
  RequestResponse,
  AuthMode,
  ActionEditorPasswordAuthGet,
  ActionEditorPublicPrivateAuthGet,
  ActionEditorStudioTokenGet,
} from "../types";
import { RootState } from "../reducers";
import { failedStudioToken, receivedStudioToken } from "../actions";
import { getErrorObj } from "../utils/errorUtils";

const debug = Debug("broadcast:sagas:auth");

const API_URL = process.env.REACT_APP_API_BASE_URL;

const editorAuthEndpoint = (
  provider: string,
  code: string,
  orgId: string,
  spaceId: string,
  arn: string
): string =>
  `${API_URL}/auth/editor/${provider}?code=${code}&orgId=${orgId}&spaceId=${spaceId}&arn=${arn}`;

const editorAuthRemoveEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const editorAuthPasswordEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const editorAuthPublicPrivateEndpoint = (
  orgId: string,
  spaceId: string,
  channelId: string
): string => `${API_URL}/${orgId}/${spaceId}/live-streams/${channelId}`;

const getStudioToken = (state: RootState): string => state.studio;

function* studioTokenGet({
  payload,
  logger,
}: ActionEditorStudioTokenGet): SagaIterator {
  debug("studioTokenGet", payload);

  try {
    const { requestAuthToken } = payload;
    const { authToken } = yield call(requestAuthToken);
    yield put(receivedStudioToken(authToken));
  } catch (error) {
    logger?.error("Failed to get Studio Token", getErrorObj(error));
    yield put(failedStudioToken(error));
  }
}

function* editorAuthGet({
  payload,
  logger,
}: ActionEditorAuthGet): SagaIterator {
  if (payload !== undefined) {
    const { provider, code, orgId, spaceId, arn } = payload;

    try {
      const result: EditorAuthResponse = yield call(
        axios.get,
        editorAuthEndpoint(provider, code, orgId, spaceId, arn)
      );
      debug("get editor auth result", result.data.data);

      const { success, data } = result.data;

      if (success) {
        yield put({
          type: ACTIONS.EDITOR_AUTH_SUCCESS,
          payload: {
            user: data.user as User,
            arn,
          },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error("Failed to fetch Auth Token", getErrorObj(error));
      yield put({
        type: ACTIONS.EDITOR_AUTH_FAILURE,
        payload: { error: error.message },
      });
    }
  }
}

function* editorAuthRemoval({
  payload,
  logger,
}: ActionEditorAuthRemove): SagaIterator {
  if (payload !== undefined) {
    const { screenCloudOrgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    payload.authMode = AuthMode.Private;

    try {
      const result: RequestResponse = yield call(
        axios.post,
        editorAuthRemoveEndpoint(screenCloudOrgId, spaceId, channelId),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("editor remove auth result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.EDITOR_AUTH_REMOVE_SUCCESS,
          payload: data.channel,
        });
      } else {
        debug("editor remove auth failure");
        logger?.error(
          `${ErrorState.FailedUpdateStream} (Auth removal - empty response)`
        );
        yield put({
          type: ACTIONS.EDITOR_AUTH_REMOVE_FAILURE,
          payload: { error: ErrorState.FailedUpdateStream },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error(
        `${ErrorState.FailedUpdateStream} (Auth removal)`,
        getErrorObj(error)
      );
      yield put({
        type: ACTIONS.EDITOR_AUTH_REMOVE_FAILURE,
        payload: {
          error: ErrorState.FailedUpdateStream,
        },
      });
    }
  }
}

function* editorPasswordAuth({
  payload,
  logger,
}: ActionEditorPasswordAuthGet): SagaIterator {
  if (payload !== undefined) {
    const { screenCloudOrgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    payload.authMode = AuthMode.Password;

    try {
      const result: RequestResponse = yield call(
        axios.post,
        editorAuthPasswordEndpoint(screenCloudOrgId, spaceId, channelId),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("editor password auth result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.EDITOR_PASSWORD_AUTH_SUCCESS,
          payload: data.channel,
        });
      } else {
        debug("editor password auth failure");
        yield put({
          type: ACTIONS.EDITOR_PASSWORD_AUTH_FAILURE,
          payload: {
            error: `${ErrorState.FailedUpdateStream} (Password Auth - empty response)`,
          },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error(
        `${ErrorState.FailedUpdateStream} (Password Auth)`,
        getErrorObj(error)
      );
      yield put({
        type: ACTIONS.EDITOR_PASSWORD_AUTH_FAILURE,
        payload: {
          error: ErrorState.FailedUpdateStream,
        },
      });
    }
  }
}

function* editorPublicPrivateAuth({
  payload,
  logger,
}: ActionEditorPublicPrivateAuthGet): SagaIterator {
  if (payload !== undefined) {
    const { screenCloudOrgId, spaceId, channelId } = payload;
    const studio = yield select(getStudioToken);
    const { token } = studio;

    try {
      const result: RequestResponse = yield call(
        axios.post,
        editorAuthPublicPrivateEndpoint(screenCloudOrgId, spaceId, channelId),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      debug("editor public private auth result", result);
      const data = extractResponse(result.data);
      if (data) {
        yield put({
          type: ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_SUCCESS,
          payload: data.channel,
        });
      } else {
        debug("editor public private auth failure");
        yield put({
          type: ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_FAILURE,
          payload: {
            error: `${ErrorState.FailedUpdateStream} (Public Private Auth - empty response)`,
          },
        });
      }
    } catch (error) {
      debug(error);
      logger?.error(
        `${ErrorState.FailedUpdateStream} (Public Private Auth)`,
        getErrorObj(error)
      );
      yield put({
        type: ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_FAILURE,
        payload: {
          error: ErrorState.FailedUpdateStream,
        },
      });
    }
  }
}

export function* studioToken(): SagaIterator {
  yield takeEvery(ACTIONS.STUDIO_TOKEN_REQUEST, studioTokenGet);
}

export function* editorAuth(): SagaIterator {
  yield takeEvery(ACTIONS.EDITOR_AUTH_REQUEST, editorAuthGet);
}

export function* editorAuthRemove(): SagaIterator {
  yield takeEvery(ACTIONS.EDITOR_AUTH_REMOVE_REQUEST, editorAuthRemoval);
}

export function* editorAuthPassword(): SagaIterator {
  yield takeEvery(ACTIONS.EDITOR_PASSWORD_AUTH_REQUEST, editorPasswordAuth);
}

export function* editorAuthPublicPrivate(): SagaIterator {
  yield takeEvery(
    ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_REQUEST,
    editorPublicPrivateAuth
  );
}
